import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TripProvider } from './contexts/TripContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { PWAInstallPrompt, PWAInstallProvider } from './components/common/PWAInstallPrompt';
import { UpdateNotification } from './components/common/UpdateNotification';
import { PageTracker } from './hooks/usePageTracking';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import CreateTrip from './pages/CreateTrip';
import TripDetail from './pages/TripDetail';
import TripOverview from './pages/TripOverview';
import TripItinerary from './pages/TripItinerary'; // Refactored - debugging
import TripPayments from './pages/TripPayments';
import TripVotes from './pages/TripVotes';
import TripMembers from './pages/TripMembers';
import TripFAQ from './pages/TripFAQ';
import TripPurchases from './pages/TripPurchases';
import TripCarpools from './pages/TripCarpools';
import TripFiles from './pages/TripFiles';
import TripFeedback from './pages/TripFeedback';
import TripWithdrawals from './pages/TripWithdrawals';
import TripShoppingLists from './pages/TripShoppingLists';
import TripGallery from './pages/TripGallery';
import TripAnalytics from './pages/TripAnalytics';
import TripSettings from './pages/TripSettings';
import TripChecklist from './pages/TripChecklist';
import TripReviews from './pages/TripReviews';
import TripTasks from './pages/TripTasks';
import TripMyTrip from './pages/TripMyTrip';
import Notifications from './pages/Notifications';
import UserSettings from './pages/UserSettings';
import TripsPage from './pages/TripsPage';
import JoinTrip from './pages/JoinTrip';
import { TripAccessGuard } from './components/guards/TripAccessGuard';

// Admin Pages
import {
  AdminLayout,
  AdminDashboard,
  AdminUsers,
  AdminTrips,
  AdminAnalytics,
  AdminActivity,
  AdminSettings,
  AdminIntegrations,
  AdminStorage,
  AdminEmailTester,
  AdminWhatsApp
} from './admin';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-slate-50 dark:bg-slate-900 safe-area">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-slate-50 dark:bg-slate-900 safe-area">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect admin to admin dashboard
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />

      {/* Join Trip Route (Public - handles auth internally) */}
      <Route path="/join/:code" element={<JoinTrip />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/trips"
        element={
          <ProtectedRoute>
            <TripsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/trips/new"
        element={
          <ProtectedRoute>
            <CreateTrip />
          </ProtectedRoute>
        }
      />

      {/* Trip Detail Routes */}
      <Route
        path="/trip/:tripId"
        element={
          <ProtectedRoute>
            <TripAccessGuard>
              <TripDetail />
            </TripAccessGuard>
          </ProtectedRoute>
        }
      >
        <Route index element={<TripOverview />} />
        <Route path="" element={<TripOverview />} />
        <Route path="my-trip" element={<TripMyTrip />} />
        <Route path="itinerary" element={<TripItinerary />} />
        <Route path="payments" element={<TripPayments />} />
        <Route path="votes" element={<TripVotes />} />
        <Route path="members" element={<TripMembers />} />
        <Route path="faq" element={<TripFAQ />} />
        <Route path="purchases" element={<TripPurchases />} />
        <Route path="carpools" element={<TripCarpools />} />
        <Route path="files" element={<TripFiles />} />
        <Route path="feedback" element={<TripFeedback />} />
        <Route path="withdrawals" element={<TripWithdrawals />} />
        <Route path="shopping" element={<TripShoppingLists />} />
        <Route path="gallery" element={<TripGallery />} />
        <Route path="analytics" element={<TripAnalytics />} />
        <Route path="settings" element={<TripSettings />} />
        <Route path="checklist" element={<TripChecklist />} />
        <Route path="reviews" element={<TripReviews />} />
        <Route path="tasks" element={<TripTasks />} />
      </Route>

      {/* Notifications */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="trips" element={<AdminTrips />} />
        <Route path="storage" element={<AdminStorage />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="email-tester" element={<AdminEmailTester />} />
        <Route path="whatsapp" element={<AdminWhatsApp />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <PWAInstallProvider>
          <AuthProvider>
            <AppWithPreferences />
          </AuthProvider>
        </PWAInstallProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

// Wrapper component that has access to auth and passes user preferences to providers
function AppWithPreferences() {
  const { user, updateUser, isAuthenticated } = useAuth();
  
  // Function to update user preferences in the backend
  const updateUserPreferences = async (preferences) => {
    if (isAuthenticated && updateUser) {
      return updateUser(preferences);
    }
  };

  return (
    <ErrorBoundary>
      <ThemeProvider user={user} updateUserPreferences={updateUserPreferences}>
        <LanguageProvider user={user} updateUserPreferences={updateUserPreferences}>
          <RealtimeProvider>
            <TripProvider>
              <PageTracker>
                <AppRoutes />
              </PageTracker>
              <Toaster 
                position="top-right" 
                richColors 
                closeButton
                toastOptions={{
                  style: {
                    fontFamily: 'Inter, sans-serif',
                  },
                }}
              />
              <PWAInstallPrompt />
              <UpdateNotification />
            </TripProvider>
          </RealtimeProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
